/* eslint-disable max-len */
/* eslint-disable no-param-reassign */
import React, { useCallback, useContext } from 'react';
import PropTypes from 'prop-types';
// eslint-disable-next-line import/no-extraneous-dependencies
import { appendMetadataWithIndex } from 'ml-portals-util-react';
import { formatCurrency } from '@jutro/components';
import { MetadataContent } from '@jutro/legacy/uiconfig';
import _ from 'lodash';
import '@xengage/gw-platform-translations';
import MLEditableCovTermMultipleOptComponent from '../MLEditableCovTermMultipleOptComponent/MLEditableCovTermMultipleOptComponent';
import metadata from './MLEditableCovTermComponent.metadata.json5';
import styles from './MLEditableCovTermComponent.module.scss';
import appConfig from 'app-config';
import { IntlContext } from '@jutro/legacy/locale';

function MLEditableCovTermComponent(props) {
    const {
        coverage,
        index,
        displayBeforeReadMoreContent,
        displayHelpMessage,
        isDescriptionDuplicated,
        hasReadMore,
        getPopoverContent,
        isSelfInsuredRetention,
        showPremium,
        showMonthlyPremium,
        monthlyPremiumEvaluator,
        isOccupancySecondaryOrSeasonal,
        isModernProduct,
        onCovChange
    } = props;

    const intl = useContext(IntlContext);
    const isQnB = appConfig.portalCode === 'QB';

    const isExtendedToPrimaryCoverage = isOccupancySecondaryOrSeasonal && (coverage?.code_ML === 'HOPCovE' || coverage?.code_ML === 'HOPCovF');

    const getCoveragePremium = useCallback((cov) => {
        if (showMonthlyPremium) {
            let monthlyAmount;
            if (monthlyPremiumEvaluator) {
                const func = monthlyPremiumEvaluator.evaluatorFunction;
                const ctx = monthlyPremiumEvaluator.context;
                monthlyAmount = func(cov, ctx);
            } else {
                monthlyAmount = cov.monthlyAmount_ML;
            }
            return monthlyAmount || '-';
        }


        if (cov.premium && _.isObject(cov.premium)) {
            return formatCurrency(intl, {value: cov.premium.amount, currency: cov.premium.currency, showFractions: false, placeholder: ''});
        }
        if (cov.premium) {
            return cov.premium;
        }

        if (cov.amount && _.isObject(cov.amount)) {
            return formatCurrency(intl, {value: cov.amount.amount, currency: cov.amount.currency, showFractions: false, placeholder: ''});
        }
        if(cov.amount){
            return cov.amount;
        }

        return 0;
    }, [intl, monthlyPremiumEvaluator, showMonthlyPremium]);

    const isYesNoOption = useCallback((term) => {
        let isYesNoOptionVal;
        if (term.hasOptions && term.options.length === 2) {
            const yesOptionAvailable = !_.isUndefined(term.options.find((opt) => opt.code === 'true'));
            const noOptionAvailable = !_.isUndefined(term.options.find((opt) => opt.code === 'false'));
            isYesNoOptionVal = yesOptionAvailable && noOptionAvailable;
        } else {
            isYesNoOptionVal = false;
        }
        return isYesNoOptionVal;
    }, []);

    const hasMultipleOptions = useCallback((term) => {
        return term.hasOptions && term.options.length > 1;
    }, []);

    const hasNoOptions = useCallback((term) => {
        return !term.hasOptions || term.options.length === 0;
    }, []);

    const getSingleOption = useCallback((term) => {
        let termValue;
        if (term.hasOptions) {
            const chosenTerm = term.options.find((opt) => opt.code === term.value);
            termValue = (chosenTerm || term.options[0]).name;
        } else {
            termValue = term.newValue;
        }
        return termValue;
    }, []);

    const getChosenTermValue = useCallback((term) => {
        let termValue;
        if (term.hasOptions) {
            const chosenTerm = term.options.find((opt) => opt.code === term.value);
            termValue = (chosenTerm || term.options[0]).name;
        } else {
            termValue = term.newValue;
        }
        return termValue;
    }, []);

    const getOptionName = useCallback((optionsTerm) => {
        if (!_.isUndefined(optionsTerm.options)) {
            const option = optionsTerm.options.find((element) => element.code === optionsTerm.value);
            return option ? option.name : '';
        }
        return undefined;
    }, []);

    const hasSingleOption = useCallback((term) => {
        return term.hasOptions && term.options.length === 1;
    }, []);

    const isTermEditable = (term) => {
        return !term.readOnly && term.isEditable_ML;
    };

    const resolvers = {
        resolveComponentMap: {
            mleditablecovtermmultipleoptcomponent: MLEditableCovTermMultipleOptComponent
        },
        resolveClassNameMap: styles
    };

    const generateTermOverrides = useCallback(() => {
        const termOverrides = coverage.terms.map((term, termIndex) => {
            return {
                [`termTableRow${index}${termIndex}`]: {
                    className: (showPremium || showMonthlyPremium)
                        ? 'table-row three-columns'
                        : `two-columns table-row ${isQnB ? 'qnb-two-column-style' : ''}`,
                    visible: term.isVisible_ML
                },

                [`covTermNameSpan${index}${termIndex}`]: {
                    content: isExtendedToPrimaryCoverage
                        ? 'Extended From Primary'
                        : term.name
                },

                [`leftColDiv${index}${termIndex}`]: {
                    className: 'ho-left-column'
                },
                [`tooltipTermContainer${index}${termIndex}`]: {
                    visible: term.description && displayHelpMessage && !isDescriptionDuplicated(term)
                },
                [`tooltipTermContainer${index}${termIndex}`]: {
                    tooltipContent: getPopoverContent(term)
                },
                [`termReadMore${index}${termIndex}`]: {
                    visible: hasReadMore(term),
                    content: isExtendedToPrimaryCoverage  ? 'Extended to Primary' : displayBeforeReadMoreContent(term)
                },
                [`termDiv${index}${termIndex}`]: {
                    className: (showPremium || showMonthlyPremium) ? 'mid-column' : 'ho-right-column',
                },
                [`mobileLabelSpan${index}${termIndex}`]: {
                    visible: !showPremium || !showMonthlyPremium
                },
                [`singleOptionTerm${index}${termIndex}`]: {
                    visible: !isExtendedToPrimaryCoverage && !isTermEditable(term) || hasSingleOption(term),
                    content: getSingleOption(term)
                },
                [`multipleOptionTerm${index}${termIndex}`]: {
                    visible: !isExtendedToPrimaryCoverage && isTermEditable(term) && !hasSingleOption(term),
                    term,
                    getChosenTermValue,
                    hasSingleOption,
                    getSingleOption,
                    isYesNoOption,
                    onCovChange,
                    hasMultipleOptions,
                    getOptionName,
                    hasNoOptions,
                    covIndex: index,
                    termIndex
                },
                [`termPremiumDiv${index}${termIndex}`]: {
                    visible: ((showPremium || showMonthlyPremium) && termIndex === 0 && !isSelfInsuredRetention(coverage))
                        || ((showPremium || showMonthlyPremium) && termIndex !== 0) || isSelfInsuredRetention(coverage),
                    content: getCoveragePremium(coverage)
                },
                [`termPremiumSpan${index}${termIndex}`]: {
                    visible: !!(showPremium || showMonthlyPremium) && termIndex === 0 && !isSelfInsuredRetention(coverage),
                    content: getCoveragePremium(coverage)
                },
                [`termValidationMsg${index}${termIndex}`]: {
                    visible: term.validationMessage_ML
                }
            };
        });

        return Object.assign({}, ...termOverrides);
    }, [coverage, index, showPremium, showMonthlyPremium, displayHelpMessage, isDescriptionDuplicated, getPopoverContent, hasReadMore, displayBeforeReadMoreContent, hasSingleOption, getSingleOption, getChosenTermValue, isYesNoOption, hasMultipleOptions, getOptionName, hasNoOptions, isSelfInsuredRetention, getCoveragePremium]);

    const overrides = {
        [`covTermLoop${index}`]: {
            data: coverage.terms
        },
        ...generateTermOverrides()
    };

    const updatedMetadata = appendMetadataWithIndex(metadata.componentContent, index);

    return <MetadataContent uiProps={updatedMetadata} overrideProps={overrides} {...resolvers} />;
}

MLEditableCovTermComponent.propTypes = {
    coverage: PropTypes.shape({}),
    index: PropTypes.number,
    getChosenTermValue: PropTypes.func,
    displayBeforeReadMoreContent: PropTypes.func,
    displayHelpMessage: PropTypes.bool,
    isDescriptionDuplicated: PropTypes.func,
    hasReadMore: PropTypes.func,
    getPopoverContent: PropTypes.func,
    isSelfInsuredRetention: PropTypes.func,
    showPremium: PropTypes.bool,
    showMonthlyPremium: PropTypes.bool,
    monthlyPremiumEvaluator: PropTypes.func,
    isOccupancySecondaryOrSeasonal: PropTypes.bool,
    isModernProduct : PropTypes.bool,
    onCovChange: PropTypes.func,
};

export default MLEditableCovTermComponent;
